import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Info, SquarePenIcon, TrashIcon } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { AddNewStoryQuestion } from '@/components/add-new-story-question-button';
import { EditableTitle } from '@/components/editable-title';
import { StoryQuestionItem } from '@/components/story-question-item';
import {
  apiPutStoryQuestionsCategoryById,
  APIPutStoryQuestionsCategoryPayload,
  apiReorderStoryQuestion,
  APIReorderStoryQuestionPayload,
  StoryQuestionsCategory,
} from '@/shared/api';
import { storyByIdQueryOptions } from '@/shared/queries';
import { Button } from '@/shared/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/shared/ui/tooltip';
import { cn, INPUT_DEBOUNCE_TIME } from '@/shared/utils';

import { DeleteStorySectionDialog } from './delete-story-section-dialog';

type StorySectionItemProps = StoryQuestionsCategory;

export const StorySectionItem = ({
  id,
  name,
  progress,
  questions,
  story: storyId,
  // description,
}: StorySectionItemProps) => {
  const intl = useIntl();
  const queryClient = useQueryClient();

  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const updateStoryQuestionsCategoryMutation = useMutation<
    StoryQuestionsCategory,
    Error,
    { id: string; payload: APIPutStoryQuestionsCategoryPayload }
  >({
    mutationFn: (data) => {
      return apiPutStoryQuestionsCategoryById(data.id, data.payload).then(
        ({ data }) => data,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: storyByIdQueryOptions(storyId).queryKey,
      });
    },
  });

  const reorderQuestionMutation = useMutation<
    { newRank: string },
    Error,
    { id: string; payload: APIReorderStoryQuestionPayload }
  >({
    mutationFn: ({ id, payload }) => {
      return apiReorderStoryQuestion(id, payload).then(({ data }) => data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: storyByIdQueryOptions(storyId).queryKey,
      });
    },
  });

  const handleChangeQuestionsCategoryName =
    (id: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;

      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      debounceTimeoutRef.current = setTimeout(() => {
        updateStoryQuestionsCategoryMutation.mutate({
          id,
          payload: { name: value },
        });
      }, INPUT_DEBOUNCE_TIME);
    };

  const hasAnswers = progress.totalAnswers > 0;
  const noAnswers = progress.totalAnswers === 0;

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const [sortableQuestions, setSortableQuestions] = useState(questions);

  useEffect(() => {
    setSortableQuestions(questions);
  }, [questions]);

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;

    if (active.id !== over?.id) {
      setSortableQuestions((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over?.id);

        reorderQuestionMutation.mutate({
          id: String(active.id),
          payload: {
            newIndex,
            categoryId: id,
          },
        });

        return arrayMove(items, oldIndex, newIndex);
      });
    }
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={sortableQuestions} strategy={verticalListSortingStrategy}>
        <div key={id} className="rounded-lg border-2 border-primary p-8">
          <div className="flex justify-between">
            <div>
              <EditableTitle
                initialTitle={name}
                trigger={<SquarePenIcon className="h-6 w-6" />}
                onChange={handleChangeQuestionsCategoryName(id)}
                placeholder={intl.formatMessage({
                  id: 'input.sectionPlaceholder',
                })}
                readonly={hasAnswers}
              >
                <p
                  className={cn('text-2xl', {
                    'text-gray-400': !name,
                  })}
                >
                  {name ??
                    intl.formatMessage({
                      id: 'input.sectionPlaceholder',
                    })}
                </p>
              </EditableTitle>

              {/* <p>{description}</p> */}
            </div>

            {noAnswers && (
              <DeleteStorySectionDialog sectionId={id} storyId={storyId}>
                <Button variant="ghost" size="icon">
                  <TrashIcon />
                </Button>
              </DeleteStorySectionDialog>
            )}

            {hasAnswers && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <Info />
                  </TooltipTrigger>

                  <TooltipContent>
                    <p>{intl.formatMessage({ id: 'storySection.noEdit.tooltip' })}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>

          <div className="mb-6 mt-4 flex flex-col gap-10">
            {sortableQuestions.map((question) => (
              <StoryQuestionItem key={question.id} storyId={storyId} {...question} />
            ))}
          </div>

          <div className="flex justify-end">
            <AddNewStoryQuestion categoryId={id} storyId={storyId} />
          </div>
        </div>
      </SortableContext>
    </DndContext>
  );
};
